import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Z } from '../../styles/zLayers';
import FeedbackButton from './FeedbackButton';
import { useBrand } from '../../contexts/BrandContext';
import {
  configApi,
  TacticDetailResponse,
  TacticFrontmatter,
  TacticCategory,
  TacticPriority,
  TacticReadiness,
  TacticPostType,
  TacticImageMode
} from '../../api/configApi';
import { getPlatformCarouselLimits } from '../../api/tendApi';
import { defaultPlacement, placementLabel, placementsFor } from '../../utils/placements';
import TacticTemplateSlot from '../config/TacticTemplateSlot';

interface TacticEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  tacticId: string;
  platform: string;
  brandSlug: string;
  onSave?: () => void;
}

const CATEGORIES: TacticCategory[] = ['Engagement', 'Story/Content Amplification', 'Down-Funnel Action', 'Brand Awareness', 'Audience Growth'];
const PRIORITIES: TacticPriority[] = ['low', 'medium', 'high'];
const READINESS_LEVELS: TacticReadiness[] = ['alpha', 'beta', 'gamma'];
const POST_TYPES: TacticPostType[] = ['single', 'carousel', 'video'];
const IMAGE_MODES: TacticImageMode[] = ['article_media', 'generated', 'none'];

type EditMode = 'brand' | 'universal';

const TacticEditModal: React.FC<TacticEditModalProps> = ({
  isOpen,
  onClose,
  tacticId,
  platform,
  brandSlug,
  onSave
}) => {
  const { isSuperAdmin, checkAccess } = useBrand();
  const canEdit = isSuperAdmin || checkAccess('editor' as any);

  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Tactic data
  const [tacticDetail, setTacticDetail] = useState<TacticDetailResponse | null>(null);
  const [editedContent, setEditedContent] = useState<string>('');
  const [editedFrontmatter, setEditedFrontmatter] = useState<TacticFrontmatter | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Mode toggle for SuperAdmin
  const [editMode, setEditMode] = useState<EditMode>('brand');

  // Platform carousel ceiling (read-only hint next to the slide bound inputs)
  const [platformCeiling, setPlatformCeiling] = useState<number | null>(null);
  useEffect(() => {
    if (isOpen && platform) {
      getPlatformCarouselLimits()
        .then((limits) => setPlatformCeiling(limits[platform.toLowerCase()] ?? null))
        .catch(() => {});
    }
  }, [isOpen, platform]);

  // Load tactic details
  useEffect(() => {
    if (isOpen && tacticId && platform) {
      loadTacticDetail();
    }
  }, [isOpen, tacticId, platform, editMode]);

  const loadTacticDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      let data;
      if (editMode === 'universal' && isSuperAdmin) {
        data = await configApi.getUniversalTacticDetail(platform, tacticId);
      } else {
        data = await configApi.getTacticDetail(brandSlug, platform, tacticId);
      }
      setTacticDetail(data);
      setEditedContent(data.content);
      setEditedFrontmatter({
        id: data.id,
        name: data.name,
        platforms: data.platforms,
        category: data.category as TacticCategory,
        priority: data.priority as TacticPriority,
        post_type: (data.post_type || 'single') as TacticPostType,
        image: (data.image || 'article_media') as TacticImageMode,
        caption: data.caption,
        link: data.link,
        hashtags: data.hashtags,
        first_comment: data.first_comment,
        thread: data.thread,
        take: !!data.take,
        slides_min: data.slides_min ?? null,
        slides_max: data.slides_max ?? null,
        placement: data.placement ?? null,
        readiness: data.readiness as TacticReadiness
      });
      setHasChanges(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tactic details');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!tacticDetail || !editedFrontmatter) return;

    try {
      setSaving(true);
      setError(null);

      if (editMode === 'universal' && isSuperAdmin) {
        await configApi.updateUniversalTactic(platform, tacticId, {
          frontmatter: editedFrontmatter,
          content: editedContent
        });
        setSuccess('Universal tactic saved');
      } else {
        await configApi.updateTactic(brandSlug, platform, tacticId, {
          frontmatter: editedFrontmatter,
          content: editedContent
        });
        setSuccess('Tactic saved');
      }

      setHasChanges(false);
      setTimeout(() => {
        setSuccess(null);
        onSave?.();
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save tactic');
    } finally {
      setSaving(false);
    }
  };

  const handleContentChange = (value: string) => {
    setEditedContent(value);
    setHasChanges(true);
  };

  const handleFrontmatterChange = (field: keyof TacticFrontmatter, value: any) => {
    if (!editedFrontmatter) return;
    setEditedFrontmatter({ ...editedFrontmatter, [field]: value });
    setHasChanges(true);
  };

  // Mirror of the backend's 422 validation
  const slideBoundsInvalid =
    editedFrontmatter?.slides_min != null &&
    editedFrontmatter?.slides_max != null &&
    editedFrontmatter.slides_min > editedFrontmatter.slides_max;

  if (!isOpen) return null;

  return createPortal(
    <div
      data-tactic-edit-modal="true"
      // Shared design-system veil (.tend-modal-veil) instead of a flat
      // bg-black/80 scrim — one place to retune the frost everywhere. z-index
      // overridden inline to sit above the tended item modal this opens over
      // (the SmartPublishButton pattern).
      className="tend-modal-veil"
      style={{ zIndex: Z.EDITOR_MODAL }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="bg-gray-900 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-bold text-white">
              {editedFrontmatter?.name || 'Edit Tactic'}
            </h2>
            <FeedbackButton entityType="tactic" entityKey={`${platform}:${tacticId}`} />
            {/* SuperAdmin Mode Toggle */}
            {isSuperAdmin && (
              <div className="flex items-center bg-gray-800 rounded-lg p-1">
                <button
                  onClick={() => setEditMode('brand')}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                    editMode === 'brand'
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Brand
                </button>
                <button
                  onClick={() => setEditMode('universal')}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                    editMode === 'universal'
                      ? 'bg-yellow-600 text-white'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Universal
                </button>
              </div>
            )}
            {/* Mode indicator */}
            {editMode === 'universal' && isSuperAdmin && (
              <span className="px-2 py-1 bg-yellow-900/50 text-yellow-300 text-xs rounded-full">
                Editing Global Defaults
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Messages */}
        {error && (
          <div className="mx-6 mt-4 bg-red-900 border border-red-700 text-red-100 px-4 py-2 rounded-lg text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="mx-6 mt-4 bg-green-900 border border-green-700 text-green-100 px-4 py-2 rounded-lg text-sm">
            {success}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            </div>
          ) : editedFrontmatter ? (
            <div className="space-y-4">
              {/* Frontmatter Form — rows 1–3 on the left, the tactic's default
                  template slot in the upper right (wraps below when narrow) */}
              <div className="flex gap-4 flex-wrap items-start">
              <div className="space-y-3 flex-1 min-w-[300px]">
                {/* Row 1: Category, Priority, Readiness, Image */}
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">Category</span>
                    <select
                      value={editedFrontmatter.category}
                      onChange={(e) => handleFrontmatterChange('category', e.target.value)}
                      disabled={!canEdit}
                      className="bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-white text-sm disabled:opacity-50 min-w-[180px]"
                    >
                      {CATEGORIES.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">Priority</span>
                    <select
                      value={editedFrontmatter.priority}
                      onChange={(e) => handleFrontmatterChange('priority', e.target.value)}
                      disabled={!canEdit}
                      className="bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-white text-sm disabled:opacity-50 min-w-[80px]"
                    >
                      {PRIORITIES.map(p => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">Readiness</span>
                    <select
                      value={editedFrontmatter.readiness}
                      onChange={(e) => handleFrontmatterChange('readiness', e.target.value)}
                      disabled={!canEdit}
                      className="bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-white text-sm disabled:opacity-50 min-w-[80px]"
                    >
                      {READINESS_LEVELS.map(r => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">Post Type</span>
                    <select
                      value={editedFrontmatter.post_type}
                      onChange={(e) => handleFrontmatterChange('post_type', e.target.value)}
                      disabled={!canEdit}
                      className="bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-white text-sm disabled:opacity-50 min-w-[95px]"
                    >
                      {POST_TYPES.map(pt => (
                        <option key={pt} value={pt}>{pt}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">Image</span>
                    <select
                      value={editedFrontmatter.image}
                      onChange={(e) => handleFrontmatterChange('image', e.target.value)}
                      disabled={!canEdit}
                      className="bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-white text-sm disabled:opacity-50 min-w-[110px]"
                    >
                      {IMAGE_MODES.map(m => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                  </div>
                  {/* Placement — only channels with more than one surface */}
                  {placementsFor(platform) && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">Placement</span>
                      <select
                        value={editedFrontmatter.placement ?? ''}
                        onChange={(e) => handleFrontmatterChange('placement', e.target.value || null)}
                        disabled={!canEdit}
                        className="bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-white text-sm disabled:opacity-50 min-w-[110px]"
                      >
                        <option value="">default ({placementLabel(defaultPlacement(platform)!, platform)})</option>
                        {placementsFor(platform)!.map(p => (
                          <option key={p} value={p}>{placementLabel(p, platform)}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {/* Row 2: Boolean toggles */}
                <div className="flex items-center gap-4 flex-wrap">
                  {[
                    { key: 'caption', label: 'Caption' },
                    { key: 'link', label: 'Link' },
                    { key: 'hashtags', label: 'Hashtags' },
                    { key: 'first_comment', label: 'First Comment' },
                    { key: 'thread', label: 'Thread' },
                    // Take (phase 1): every tend from this tactic asks for a
                    // to-camera recording and lands in the brand's Take queue.
                    // Independent of Post Type — a carousel can be a take.
                    { key: 'take', label: 'Take', hint: 'Every tend from this tactic asks for a to-camera recording with the teleprompter and lands in the Take queue' },
                  ].map(toggle => (
                    <label key={toggle.key} className="flex items-center gap-1.5 cursor-pointer" title={toggle.hint}>
                      <input
                        type="checkbox"
                        checked={!!(editedFrontmatter as any)[toggle.key]}
                        onChange={(e) => handleFrontmatterChange(toggle.key as keyof TacticFrontmatter, e.target.checked)}
                        disabled={!canEdit}
                        className="w-4 h-4 rounded bg-gray-700 border-gray-600 text-blue-600 disabled:opacity-50"
                      />
                      <span className="text-sm text-gray-300">{toggle.label}</span>
                    </label>
                  ))}
                </div>

                {/* Row 3: Carousel slide bounds (carousel tactics only) */}
                {editedFrontmatter.post_type === 'carousel' && (
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">Slides min</span>
                      <input
                        type="number"
                        min={1}
                        max={35}
                        value={editedFrontmatter.slides_min ?? ''}
                        onChange={(e) => { const v = parseInt(e.target.value, 10); handleFrontmatterChange('slides_min', Number.isFinite(v) ? v : null); }}
                        disabled={!canEdit}
                        placeholder="—"
                        className="bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-white text-sm disabled:opacity-50 w-16"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">Slides max</span>
                      <input
                        type="number"
                        min={1}
                        max={35}
                        value={editedFrontmatter.slides_max ?? ''}
                        onChange={(e) => { const v = parseInt(e.target.value, 10); handleFrontmatterChange('slides_max', Number.isFinite(v) ? v : null); }}
                        disabled={!canEdit}
                        placeholder="—"
                        className="bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-white text-sm disabled:opacity-50 w-16"
                      />
                    </div>
                    {platformCeiling != null && (
                      <span
                        className="text-xs text-gray-500"
                        title="Hard ceiling enforced by the publishing API"
                      >
                        Platform max: {platformCeiling}
                      </span>
                    )}
                    {slideBoundsInvalid && (
                      <span className="text-xs text-red-400">min cannot exceed max</span>
                    )}
                  </div>
                )}
              </div>
              {/* Overrides the post-type Default Templates (Design ▾) for this
                  tactic; saves on pick, independent of Save Changes. */}
              <TacticTemplateSlot
                platform={platform}
                tacticId={tacticId}
                postType={editedFrontmatter.post_type}
                take={!!editedFrontmatter.take}
                editMode={editMode === 'universal' && isSuperAdmin ? 'universal' : 'brand'}
                canEdit={canEdit}
                width={150}
              />
              </div>

              {/* Content Editor */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Content (Description, Template, Examples)
                </label>
                <textarea
                  value={editedContent}
                  onChange={(e) => handleContentChange(e.target.value)}
                  disabled={!canEdit}
                  className="w-full h-80 bg-gray-800 border border-gray-700 rounded-lg p-4 text-white font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  placeholder="# Tactic Name&#10;&#10;## Description&#10;...&#10;&#10;## Template&#10;...&#10;&#10;## Examples&#10;- ..."
                />
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-12">
              No tactic data available
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-700 bg-gray-800/50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
          >
            Cancel
          </button>
          {canEdit && (
            <button
              onClick={handleSave}
              disabled={saving || !hasChanges || slideBoundsInvalid}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                saving || !hasChanges || slideBoundsInvalid
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TacticEditModal;
